import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';

const HEADER_OFFSET = 80;
const FALLBACK_MS = 1500;

function stripBase(path: string) {
  const base = `/${__BASE_PATH__.split('/').filter(Boolean).join('/')}`;
  if (base !== '/' && path.startsWith(base)) return path.slice(base.length) || '/';
  return path;
}

function nextFrames(n: number) {
  return new Promise<void>((resolve) => {
    const step = (left: number) => (left <= 0 ? resolve() : requestAnimationFrame(() => step(left - 1)));
    step(n);
  });
}

function scrollToTarget(hash: string) {
  if (hash) {
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
      window.scrollTo(0, Math.max(0, y));
      return;
    }
  }
  window.scrollTo(0, 0);
}

export default function PageTransition() {
  const navigate = useNavigate();
  const layerRef = useRef<HTMLDivElement>(null);
  const busy = useRef(false);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return undefined;
    gsap.set(layer, { yPercent: 100, visibility: 'hidden' });

    const finish = () => {
      gsap.killTweensOf(layer);
      gsap.set(layer, { yPercent: 100, visibility: 'hidden' });
      busy.current = false;
    };

    const run = async (to: string, hash: string) => {
      busy.current = true;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) {
        navigateRef.current(to);
        await nextFrames(2);
        scrollToTarget(hash);
        busy.current = false;
        return;
      }
      const safety = window.setTimeout(finish, FALLBACK_MS);
      try {
        gsap.set(layer, { visibility: 'visible', yPercent: 100 });
        await gsap.to(layer, { yPercent: 0, duration: 0.28, ease: 'power2.inOut' });
        navigateRef.current(to);
        await nextFrames(2);
        scrollToTarget(hash);
        await nextFrames(1);
        await gsap.to(layer, { yPercent: -100, duration: 0.32, ease: 'power2.inOut' });
      } finally {
        window.clearTimeout(safety);
        finish();
      }
    };

    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a');
      if (!a || !a.href) return;
      if (a.target && a.target !== '_self') return;
      if (a.hasAttribute('download') || a.dataset.noTransition !== undefined) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const samePath = url.pathname === window.location.pathname;
      if (samePath) return; // same page / anchor: native behavior
      e.preventDefault();
      e.stopPropagation();
      if (busy.current) return;
      run(stripBase(url.pathname) + url.search + url.hash, url.hash);
    };

    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('click', onClick, true);
      finish();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={layerRef}
      aria-hidden="true"
      className="page-transition-layer fixed left-0 top-0 w-full pointer-events-none"
      style={{ zIndex: 9999, height: '100lvh', minHeight: '100vh', background: '#050506', visibility: 'hidden' }}
    />
  );
}